// Angular
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

// PrimeNG
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputOtpModule } from 'primeng/inputotp';
import { StepperModule } from 'primeng/stepper';

// QR Code
import * as QRCode from 'qrcode';

// Modèles
import { MfaSetupResponse } from 'src/app/@core/authentication/services/mfa.service';

// Composants
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';
import { MfaInfoComponent } from 'src/app/@shared/components/mfa/mfa-info/mfa-info.component';

@Component({
    selector: 'app-mfa-setup-form',
    imports: [
        ReactiveFormsModule,
        AutoFocusModule,
        ButtonModule,
        InputTextModule,
        InputOtpModule,
        StepperModule,
        MfaInfoComponent,
    ],
    templateUrl: './mfa-setup-form.component.html',
    styleUrls: ['./mfa-setup-form.component.scss']
})
export class MfaSetupFormComponent extends FormBaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @Input() preAuthToken?: string;
    // Enrôlement depuis le profil : le jeton est un jeton de ré-authentification (`manage`),
    // son expiration redemande la ré-authentification au lieu de renvoyer à la connexion
    @Input() manageMode: boolean = false;
    @Output() mfaSuccess = new EventEmitter<void>();
    @Output() reauthRequired = new EventEmitter<void>();
    @Output() sessionExpired = new EventEmitter<void>();

    private _currentStep: number = 1;
    public get currentStep(): number {
        return this._currentStep;
    }
    public set currentStep(value: number) {
        this._currentStep = value;
    }

    public qrCodeDataUrl: string = '';
    public secret: string = '';
    public showSecret: boolean = false;

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _deviceId: number = 0;

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public setupDevice(): void {
        const deviceName = this.form.get('deviceName')?.value as string;
        this._setLoading(true);
        this.mfaService.setup$(deviceName, this.preAuthToken)
            .pipe(finalize(() => this._setLoading(false)))
            .subscribe({
                next: (response: MfaSetupResponse) => {
                    this._deviceId = response.deviceId;
                    this.secret = response.secret;
                    QRCode.toDataURL(response.otpauthUrl, { width: 220, margin: 2 }).then(url => {
                        this.qrCodeDataUrl = url;
                        this.currentStep = 2;
                    });
                },
                error: (err: any) => {
                    if (err?.status === 401) {
                        this._emitUnauthorized();
                    }
                }
            });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override buildForm() {
        this.form = this.formBuilder.group({
            deviceName: ['Mon téléphone', Validators.required],
            code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
        });
    }

    protected override subscribeToObservables(): void {
        this.subscriptions.add(
            this.form.get('code')?.valueChanges.subscribe(() => {
                if (this.form.get('code')?.valid) {
                    this._confirmCode();
                }
            })
        );
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _confirmCode(): void {
        const code = this.form.get('code')?.value as string;
        this._setLoading(true);
        this.mfaService.confirm$(this._deviceId, code, this.preAuthToken)
            .pipe(finalize(() => this._setLoading(false)))
            .subscribe({
                next: () => this.mfaSuccess.emit(),
                error: (err: any) => {
                    if (err?.status === 401) {
                        this._emitUnauthorized();
                    } else {
                        this.messageService.sendMessage({
                            severity: 'error',
                            summary: 'Code invalide',
                            detail: 'Le code saisi est invalide. Vérifiez votre application et réessayez.',
                            life: 5000
                        });
                        this.form.get('code')?.reset();
                    }
                }
            });
    }

    private _emitUnauthorized(): void {
        if (this.manageMode) {
            this.reauthRequired.emit();
        } else {
            this.sessionExpired.emit();
        }
    }

    private _setLoading(loading: boolean): void {
        this.loading = loading;
        if (loading) {
            this.form.disable({ emitEvent: false });
        } else {
            this.form.enable({ emitEvent: false });
        }
    }
}
